type BulkActionContext = any;

export type BulkActionOnProgress = (progress: {
  total: number;
  processed: number;
  successful: number;
  failed: number;
}) => void;

export function bulkActionFactory<T = any>({
  action,
  onStart,
  onFinish,
  onProgress,
  concurrentLimit = 16
}: {
  action: (item: T, context?: BulkActionContext) => Promise<any>;
  onStart?: () => void;
  onFinish?: (result: { successItems: T[]; failedItems: T[] }) => void;
  onProgress?: BulkActionOnProgress;
  concurrentLimit?: number;
}) {
  const run = async (
    items: T[],
    context?: BulkActionContext
  ): Promise<{
    successItems: T[];
    failedItems: T[];
  }> => {
    await onStart?.();

    const successItems: T[] = [];
    const failedItems: T[] = [];

    const requests = [...items];
    const total = items.length;

    async function next(...batchItems: T[]) {
      await Promise.all(
        batchItems.map(async item => {
          try {
            await action(item, context);
            successItems.push(item);
          } catch {
            failedItems.push(item);
          }

          onProgress?.({
            total,
            processed: successItems.length + failedItems.length,
            successful: successItems.length,
            failed: failedItems.length
          });

          const nextItem = requests.pop();

          if (nextItem) {
            await next(nextItem);
          }
        })
      );
    }

    await next(...requests.splice(0, concurrentLimit));

    await onFinish?.({
      successItems,
      failedItems
    });

    return {
      successItems,
      failedItems
    };
  };

  return {
    run
  };
}
